/**
 * Core Layer - Canvas Transformation Utilities
 * Handles canvas flipping, rotation, and transformations
 * No state management, no business logic - pure functions only
 */

/**
 * Create a vertically and/or horizontally flipped canvas
 */
export function getVerticallyFlippedCanvas(canvas, isVertically, isHorizontally) {
    const {width, height} = canvas;
    const offscreenCanvas = new OffscreenCanvas(width, height);

    const offscreenCtx = offscreenCanvas.getContext('2d');
    offscreenCtx.translate(isHorizontally ? width : 0, isVertically ? height : 0);
    offscreenCtx.scale(isHorizontally ? -1 : 1, isVertically ? -1 : 1);
    offscreenCtx.drawImage(canvas, 0, 0, width, height);

    return offscreenCanvas;
}

/**
 * Draw a rotated frame on canvas (90 degrees counterclockwise)
 */
export function drawRotatedFrame(canvas, frame) {
    const context = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    const radians = Math.PI * 3 / 2;
    const centerX = width / 2;
    const centerY = height / 2;

    context.clearRect(0, 0, width, height);
    context.save();
    context.translate(centerX, centerY);
    context.rotate(radians);
    context.translate(-centerY, -centerX);
    context.drawImage(frame, 0, 0, height, width);
    context.restore();
}

/**
 * Get a flipped canvas context (for mirroring)
 */
export function getFlippedCanvasContext(canvasRef) {
    if (!canvasRef || !canvasRef.current) {
        return null;
    }

    const context = canvasRef.current.getContext('2d');
    const width = canvasRef.current.width;

    context.translate(width, 0);
    context.scale(-1, 1);

    return context;
}

/**
 * Calculate canvas display style based on dimensions and transformations
 */
export function getCanvasStyle(canvasRef, height, width, needReverse, isHoloLens, shouldFlipHorizontally = false) {
    const style = {};

    if (!canvasRef) {
        return style;
    }

    const {clientWidth: containerWidth, clientHeight: containerHeight} = canvasRef.parentElement || canvasRef;

    style.objectFit = 'contain';

    if (height > width) {
        style.width = '100%';
    } else {
        style.height = '100%';
    }

    style.maxHeight = containerHeight;
    style.maxWidth = containerWidth;

    if (needReverse && !isHoloLens) {
        if (shouldFlipHorizontally) {
            style.transform = 'rotateX(180deg) rotateY(180deg)';
        } else {
            style.transform = 'rotateX(180deg)';
        }
    } else if (shouldFlipHorizontally) {
        style.transform = 'rotateY(180deg)';
    } else {
        style.transform = 'none';
    }

    return style;
}

/**
 * Calculate real dimensions of canvas accounting for black stripes
 */
export function getBigCanvasRealDimensions(canvasRef) {
    if (!canvasRef || !canvasRef.current) {
        return {blackStripeHeight: 0, blackStripeWidth: 0, actualHeight: 0, actualWidth: 0};
    }

    const canvas = canvasRef.current;
    const realWidth = canvas.width;
    const realHeight = canvas.height;
    const blackStripesOnHeight = realWidth > realHeight;

    let blackStripeHeight = 0;
    let blackStripeWidth = 0;
    let actualHeight;
    let actualWidth;

    if (blackStripesOnHeight) {
        actualWidth = canvas.clientWidth;
        const ratio = actualWidth / realWidth;
        actualHeight = ratio * realHeight;
        blackStripeHeight = (canvas.clientHeight - actualHeight) / 2;
    } else {
        actualHeight = canvas.clientHeight;
        const ratio = actualHeight / realHeight;
        actualWidth = ratio * realWidth;
        blackStripeWidth = (canvas.clientWidth - actualWidth) / 2;
    }

    return {blackStripeHeight, blackStripeWidth, actualHeight, actualWidth};
}
